/**
 * The arithmetic behind the Ledger tab (MED-967/969).
 *
 * Kept out of the component so it can be tested without a DOM, because these are the parts that
 * can be quietly wrong: a running balance that counts a duplicate, or a reconciliation check
 * that calls an overpayment a fault.
 *
 * Nothing here decides WHAT counts towards a balance - the API answers that per row, in
 * `countsTowardsBalance`, from the same rule InvoiceTransactionRepository and the reconciliation
 * command use. Reimplementing "duplicate or awaiting review" here is exactly how the screen and
 * the command would end up disagreeing about what an invoice is owed.
 */

// The duplicate badge lives in duplicates.js rather than here (MED-1023), because Payment
// Activity labels the same states and the two tabs had drifted apart about how.
import { duplicateBadge } from './duplicates';

/**
 * Human-readable labels for the transaction types, so the screen does not show BROUGHT_FORWARD.
 */
export const LEDGER_TYPE_LABELS = {
  BROUGHT_FORWARD: 'Brought forward',
  CHARGE: 'Charge',
  PAYMENT: 'Payment',
  REFUND: 'Refund',
  CREDIT_NOTE: 'Credit note',
  WRITE_OFF: 'Write-off',
  ADJUSTMENT: 'Adjustment',
  REVERSAL: 'Reversal',
};

export const ledgerTypeLabel = (type) => LEDGER_TYPE_LABELS[type] ?? type;

/**
 * Decorate each transaction with the balance the invoice stood at after it.
 *
 * The running balance is the reason this tab exists: the old payment list can say what happened,
 * but only this can say how the balance got to where it is.
 *
 * Rows that do not count keep the previous balance, so a duplicate is visibly present and
 * visibly inert - the balance column simply does not move past it.
 */
export const buildLedgerRows = (transactions) => {
  if (!Array.isArray(transactions) || transactions.length === 0) {
    return [];
  }

  let runningBalance = 0;

  return transactions.map((transaction) => {
    if (transaction.countsTowardsBalance) {
      runningBalance += transaction.amount;
    }

    return {
      ...transaction,
      ...duplicateBadge(transaction, { noun: 'transaction' }),
      typeLabel: ledgerTypeLabel(transaction.type),
      runningBalance,
    };
  });
};

/**
 * Does the ledger agree with the balance the invoice is actually being chased on?
 *
 * This is the whole point of showing the ledger during phase 1. The balance is still written
 * directly and the ledger is written alongside it, so this comparison is the check that the
 * dual-write is right - made by the person looking at the invoice, who knows what it should say,
 * rather than only by a scheduled command.
 *
 * `overpayment` separates the one disagreement that is not a fault. Invoice::setOutstandingBalance()
 * clamps a negative balance to zero, so an overpaid invoice always reads as stored-zero against a
 * negative ledger. That is the overpayment the clamp has been hiding, not a missed transaction,
 * and the screen should say so rather than crying wolf. MED-970 removes the clamp.
 */
export const reconcileLedger = (transactions, storedBalance) => {
  const ledgerTotal = (transactions ?? [])
    .filter((transaction) => transaction.countsTowardsBalance)
    .reduce((total, transaction) => total + transaction.amount, 0);

  const stored = storedBalance ?? 0;

  return {
    ledgerTotal,
    storedBalance: stored,
    difference: ledgerTotal - stored,
    agrees: ledgerTotal === stored,
    overpayment: stored === 0 && ledgerTotal < 0,
  };
};

/**
 * What to say when an invoice has no ledger rows at all.
 *
 * This is the common case, not the edge case: the MED-968 backfill opens a ledger only for
 * invoices that are owed something, so 87,164 of 94,369 invoices have none. The tab is shown
 * anyway, because a tab that appears on one invoice in twelve reads as a bug rather than as a
 * feature, and because the explanation is worth giving.
 *
 * The two cases are genuinely different, and only one of them is benign:
 *
 *   settled - nothing is owed, so an empty ledger is the correct and complete answer. Its
 *             payment history predates the ledger and lives on the Payment Activity tab.
 *
 *   missing - something IS owed and the ledger cannot account for it. That is the drift
 *             condition: app:ledger:reconcile would report this invoice, so the screen says so
 *             too rather than reassuring somebody that all is well.
 *
 * `isFault` is the whole distinction: it picks the styling and it is what the two cases actually
 * differ by. A separate label naming the case would only ever be read by its own test.
 */
export const emptyLedgerState = (invoice) => {
  const balance = invoice?.outstandingBalance ?? 0;

  if (balance === 0) {
    return {
      isFault: false,
      message:
        'Nothing is owed on this invoice, so it has no ledger. It was already settled when the ledger opened - its payment history is on the Payment Activity tab.',
    };
  }

  return {
    isFault: true,
    message:
      'This invoice is owed money but has no ledger transactions, so the ledger cannot account for its balance. The outstanding balance is still the figure being chased, so nothing is wrong with the invoice itself - but please report this.',
  };
};
